import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2.5 whitespace-nowrap font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-success-40 text-white hover:bg-success-50',
        secondary: 'bg-white text-gray-80 border border-gray-30 hover:bg-gray-5',
        outline: 'border border-gray-30 bg-transparent hover:bg-gray-10 text-gray-60',
        ghost: 'hover:bg-gray-10 text-gray-60',
        link: 'text-brand-60 underline-offset-4 hover:underline',
        brand: 'bg-brand-60 text-white hover:bg-brand-40',
      },
      size: {
        default: 'h-12 px-5 py-3',
        sm: 'h-10 px-4 py-2 text-sm',
        lg: 'h-14 px-6 py-4',
        icon: 'h-10 w-10',
      },
      rounded: {
        default: 'rounded-full',
        md: 'rounded-xl',
        lg: 'rounded-2xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      rounded: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, rounded, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, rounded, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
